/*
 * Takes an input and returns it as formatted currency
 * Uses on Intl.NumberFormat
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat
*/
import { CurrencyDisplay } from '@/utils/types';

export function _currencyFormatter(value: string, currency: string = 'USD', currencyDisplay: CurrencyDisplay = 'symbol', locale: string = navigator.language, hasImpliedDecimal: boolean = true, formatToParts: boolean = false) {
    let isNegative: boolean = false;
    let filtered: string = '';
    let filteredAsNumber: number = 0;

    // This test is for iOS as it doesn't support 'narrowSymbol' as an option. If the NumberFormat
    // returns an error, we test for that particular error and change currencyDisplay to 'symbol'
    try {
        new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay }).format(123);
    } catch (error) {
        if (error instanceof Error) {
            if (error.message === 'RangeError: currencyDisplay must be either "code", "symbol", or "name"') {
                currencyDisplay = 'symbol';
            }
        }
    }

    if (typeof(value) !== 'string') {
        console.error('Error: currencyFormatter requires an input value of type \'string\''); // tslint:disable-line
        return '';
    }

    if (value.charAt(0) === '-') { // If the first character is a dash (for a negative number)
        isNegative = true; // Set flag for later
    }

    if (value.charAt(0) === '.') { // If the first character is a dot (for a decimal)
        if (value.length === 1) { // If a period was the only character passed in
            return '';
        } else {
            value = `0${value}`; // This will help make it into a value that can be converted
        }
    }

    filtered = value.replace(/[^0-9\.]/g, ''); // This will remove everything except numbers and periods

    if (filtered === '') { // If the string is blank, we can quit here
        return '';
    }

    // Determine if the selected currency uses decimal places
    const maxFracDigits = new Intl.NumberFormat(locale, { style: 'currency', currency}).resolvedOptions().maximumFractionDigits!;
    const currencyHasDecimals = maxFracDigits > 0;

    if (hasImpliedDecimal && currencyHasDecimals) { // If currency uses decimals, we fill numbers from the right
        filtered = filtered.replace(/\D/g, ''); // Remove all non-digits

        if (Number(filtered) !== 0) {
            const padded = filtered.padStart(maxFracDigits + 1, '0'); // Left fill zeros
            const decimalIndex = padded.length - maxFracDigits; // Find where decimal place goes

            filtered = `${padded.slice(0, decimalIndex)}.${padded.slice(decimalIndex)}`; // Reconstruct full number
        }
    }

    if (isNegative) { // If the value was initially negative, let's add it back here
        filtered = `-${filtered}`;
    }

    filteredAsNumber = Number(filtered.replace(/[^0-9\.\-]/g, '')); // Recast the filtered value as a number

    if (isNaN(filteredAsNumber)) { // Prevent converting and returning 'NaN'
        console.error('Error: currencyFormatter: invalid input resulted in NaN'); // tslint:disable-line
        return '';
    }

    try { // Convert the number into the desired output
        if (!formatToParts) {
            return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay }).format(filteredAsNumber);
        } else {
            return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay }).formatToParts(filteredAsNumber) as object[];
        }
    } catch (error) {
        console.error(error); // tslint:disable-line
        return '';
    }
}